<template>
  <div class="social-step-editor">
    <shared-step-editor-header
      :enabled="stepStatus"
      :confirmation="canUndo"
      class="social-step-editor__header"
      @exit="$emit('close')"
      @status="onSetStatus"
    >
      <template #name>
        <shared-step-editor-header-name :name="step.name">
          <i :class="iconClass" class="social-step-editor__logo"></i>
        </shared-step-editor-header-name>
      </template>
      <template #editor> &nbsp; </template>

      <template #actions>
        <social-step-editor-header-actions
          :advanced-mode="advancedMode"          
          :pieces="socialPieces" 
          :social="socialNetwork" 
          :saving="saving"
          :link-dangerous="linkDangerous"
          :loading="loading || isEmpty"
          @save="onSave"
          @advanced="enterAdvancedModalOpen = true"
          @classic="leaveAdvancedModalOpen = true"
          @exit="onCloseEditor"
        />
      </template>
    </shared-step-editor-header>     
    <social-step-editor-main-wrapper @undo="onUndo()" @redo="onRedo()" :canUndo="canUndo" :canRedo="canRedo">
      <social-step-editor-main
        v-if="socialPieces.length" 
        :pieces="socialPieces"
        :activity-type="activityType"
        :date="date"
        :profile="profile"
        :advanced-mode="advancedMode"
        :default-view="defaultView"
        :image="imagePieceContent"
      />
      <div v-else class="social-step-editor__loader">
      <shared-step-editor-loader  :size="100"  />
      </div>
    </social-step-editor-main-wrapper>

    <social-step-editor-sidebar>
      <template v-if="socialPieces.length">
        <social-step-editor-sidebar-text-fields
          v-if="activeMenuItem.name === menuType.TextFields && socialPieces.length"
          :pieces="socialPieces"
          :activity-type="activityType"
          :failed="failed"
          :advanced-mode="advancedMode"
          @update="onUpdatePiece"
        />
        <social-step-editor-sidebar-media
          v-if="activeMenuItem.name === menuType.Media"
          :image-piece="imagePiece"
          :stock-open="stockOpen"
          :ai-open="aiOpen"
          :upload-open="uploadOpen"   
          :advanced-mode="advancedMode"      
          @stock-tab="onStockTab"
          @ai-tab="onAiTab"
          @upload-tab="onUploadTab"
          @update="onUpdatePiece"
        />
        <social-step-editor-sidebar-advanced
          v-if="activeMenuItem.name === menuType.AdvancedMode && linkPiece"
          :piece="linkPiece"
          :image="imagePieceContent"
          :link-dangerous="linkDangerous"
          :message="advancedNotification"
          @pick="onPickImage"         
          @change-link="onUpdateLink"
          @close="leaveAdvancedModalOpen = true"  
        >
        </social-step-editor-sidebar-advanced>
      </template>
      <div v-else class="social-step-editor__loader">
        <shared-step-editor-loader  :size="100"  />
      </div>
    </social-step-editor-sidebar>

    <social-step-editor-menu
      :items="menuItems"
      :active-item="activeMenuItem"
      :disabled-items="disabledMenuItems"
      @clicked="onUpdateMenu"
    />
    <modal-social-enter-advanced
      :open="enterAdvancedModalOpen"
      @confirm="onAdvanced"
      @close="enterAdvancedModalOpen = false"
    />
    <modal-social-leave-advanced
      :open="leaveAdvancedModalOpen"
      @confirm="onExit"
      @close="leaveAdvancedModalOpen = false"
    />
    <span v-html="hideScroll"></span>
  </div>
</template>

<script>
import SharedStepEditorHeader from '../shared/SharedStepEditorHeader';
import SocialStepEditorHeaderActions from './header/SocialStepEditorHeaderActions';
import SocialStepEditorMenu from './menu/SocialStepEditorMenu';
import SharedStepEditorHeaderName from '../shared/SharedStepEditorHeaderName';
import SocialStepEditorMain from './main/SocialStepEditorMain.vue';
import SocialStepEditorMainWrapper from './main/SocialStepEditorMainWrapper.vue';
import SocialStepEditorSidebar from './sidebar/SocialStepEditorSidebar.vue';
import SocialStepEditorSidebarTextFields from './sidebar/SocialStepEditorSidebarTextFields.vue';
import SocialStepEditorSidebarMedia from './sidebar/SocialStepEditorSidebarMedia.vue';
import SocialStepEditorSidebarAdvanced from './sidebar/advanced/SocialStepEditorSidebarAdvanced.vue';
import TextSvg from '!!raw-loader!../../../../../../assets/images/builders/text.svg';
import ImagesSvg from '!!raw-loader!../../../../../../assets/images/builders/images.svg';
import AdvancedSvg from '!!raw-loader!../../../../../../assets/images/builders/advanced.svg';
import ModalSocialEnterAdvanced from './modals/ModalSocialEnterAdvanced.vue';
import ModalSocialLeaveAdvanced from './modals/ModalSocialLeaveAdvanced.vue';
import {SocialPostTypes} from './SocialPostTypes.js';
import {socialStepEditorMenuType} from './menu/SocialStepEditorMenuType.js';
import {getSocialNetworkProfile} from '../../../../utility/api/vessel/getSocialNetworkProfile.js';
import {SocialPostPieceNames} from './SocialPostPieceNames.js';
import {uploadGalleryImage} from '../../../../utility/gallery/uploadGalleryImage';
import {builderMixin} from '../shared/mixins/builder.mixin.js';
import {getCampaignStepPieces} from '../../../../utility/api/campaign/getCampaignStepPieces';
import SharedStepEditorLoader from '../shared/SharedStepEditorLoader';
import {undoRedo} from '../shared/mixins/undoRedo.mixin.js';
import {enterAdvancedMode} from '../../../../utility/api/campaign/enterAdvancedMode.js';
import {leaveAdvancedMode} from '../../../../utility/api/campaign/leaveAdvancedMode.js';
import {isValidUrl} from '../../../../utility/isValidUrl.js';
import {generateUuid} from '../../../../utility/generateUuid';
import * as Sentry from "@sentry/vue";

export default {
  name: 'SocialStepEditor',
  mixins: [undoRedo, builderMixin],
  components: {
    SharedStepEditorHeader,
    SharedStepEditorHeaderName,
    SocialStepEditorHeaderActions,
    SocialStepEditorMenu,
    SocialStepEditorSidebar,
    SocialStepEditorSidebarTextFields,
    SocialStepEditorSidebarMedia,
    SocialStepEditorSidebarAdvanced,
    SocialStepEditorMain,
    SocialStepEditorMainWrapper,
    SharedStepEditorLoader,
    ModalSocialEnterAdvanced,
    ModalSocialLeaveAdvanced,
  },
  props: {
    step: {
      type: Object,
      required: true,
    },
    campaign: {
      type: Object,
      required: true,
    },        
    activityType: {
      type: String,
      required: true,
    },
    date: Number,
    stepStatus: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    menuType: socialStepEditorMenuType,
    activeMenuItem: null,
    socialPieces: [],
    hideScroll: `<style>body {overflow: hidden}</style>`,
    profile: null,
    failed: false,
    steps: {
      [SocialPostTypes.FacebookPagePosts]: 'facebook',
      [SocialPostTypes.FacebookPersonalPosts]: 'facebook',
      [SocialPostTypes.LinkedInPersonalPosts]: 'linkedin',
      [SocialPostTypes.TwitterPersonalPosts]: 'twitter',
    },    
    pieces: [],
    saving: false,
    stockOpen: false,
    aiOpen: false,
    uploadOpen: false,
    excludedFields: [SocialPostPieceNames.CallToAction],
    enterAdvancedModalOpen: false,
    leaveAdvancedModalOpen: false,
    advancedMode: false,
    defaultView: true,
    prevLink: '',
    linkDangerous: false,
    loading: false,
    isEmpty: false,
    advancedNotification: ''
  }),
  created() {
    this.activeMenuItem = this.menuItems[0];
  },
  methods: { 
    async checkLinkVulnerability(link) {
      let clearLink = link.replace('www.', '')
      const url = encodeURIComponent(clearLink);
      try {
        const response = await axios.get(`/api/?entity=utility&action=getLinkVulnerabilityScore&url=${url}`);
        return response.data;
      } catch (error) {
        Sentry.captureException(error);
        this.advancedNotification = 'We unable to check this link. Something went wrong';             
      }
    },
    async onCheckLink(link) {      
      this.loading = true;   
      this.onChangeLinkStatus(false);  
      this.advancedNotification = '';
      try {        
        const data = await this.checkLinkVulnerability(link);
          setTimeout(() => {
            if (data?.risk_score > 0) {
              this.advancedNotification = 'We found your link being potentially malicious. Please use another one.';             
              this.onChangeLinkStatus(true);
            } 
          }, 200);
      } catch(err) {}
      finally {
        this.loading = false;        
      }      
    },
    onStockTab(open) {
      this.stockOpen = open
    },
    onAiTab(open) {
      this.aiOpen = open
    },
     onUploadTab(open) {
      this.uploadOpen = open
    },  
    onChangeLinkStatus(status) {
      this.linkDangerous = status;
    },
    onCloseEditor() {
      document.querySelector('#shared-step-editor-header-close')?.click();
    },
    onPickImage() {
      this.onUpdateMenu(this.menuType.Media);
    },    
    clearLinkPiece() {
      const newLinkPiece = {...this.linkPiece, content: ''}      
      this.onUpdatePiece(newLinkPiece);
      this.prevLink = '';
    },
    async onClassic() { 
      await this.getPieces();      
      this.leaveAdvancedModalOpen = false;
      this.onUpdateMenu(this.menuType.TextFields);
      this.advancedMode = false;
      this.clearUndoRedo();
      this.updateUndoRedo(this.socialPieces);
    },
    async onAdvanced() {      
      if (this.defaultView) {
        this.defaultView = false;
      }
      await this.addTemporaryLinkPiece();
         
      if(this.classicLink || !this.linkPieceContent?.length) {        
        this.clearLinkPiece()
      }
      this.enterAdvancedModalOpen = false;
      this.advancedMode = true;
      this.onUpdateMenu(this.menuType.AdvancedMode);
      this.clearUndoRedo();
      this.updateUndoRedo(this.socialPieces);
    },
    onExit() {
      this.defaultView = true;
      this.onClassic();
    },
    onUndo() {
      this.undo();
      this.socialPieces = this.state;
    },
    onRedo() {
      this.redo();
      this.socialPieces = this.state;
    },
    onUpdateMenu(name) {     
      if(name === socialStepEditorMenuType.AdvancedMode && !this.advancedMode) {
        this.enterAdvancedModalOpen = true;
        return;
      } 
      this.activeMenuItem = this.menuItems.find(i => i.name === name);
    },
    async onUpdateLink(link) {      
      if (this.prevLink.trim() === link.trim() && link.length) {
        return;
      }
      this.prevLink = link;
      const item = {id: this.linkPiece.id, content: link};
      this.onUpdatePiece(item);
    },
    onUpdatePiece(item) {   
      this.socialPieces = this.socialPieces.map(piece =>
        piece.id === item.id ? {...piece, content: item.content} : piece
      );      
      this.updateUndoRedo(this.socialPieces);
      this.updateSaveButton();
    },
    updateSaveButton() {
      let image       = this.findNewPiece(SocialPostPieceNames.ImagePreview);
      let link        = this.findNewPiece(SocialPostPieceNames.Link);
      let msg         = this.findNewPiece(SocialPostPieceNames.PersonalMessage) || this.findNewPiece(SocialPostPieceNames.Message);

      this.isEmpty = !msg && !image && !(link && !this.defaultView);
    },
    findNewPiece(name) {
      return this.socialPieces.find(piece => piece.name === name)?.content?.trim();
    },
    async convertPixabayLinkToAWS(url) {
      const response = await fetch(url);
        let data = await response.blob();        
        let file = new File([data], "pixabay_image.jpg", {type: 'image/jpeg'});
        const link = await uploadGalleryImage(file);
        return link;
    },
    async getImageLink() {
      if(this.isPixabayImage) {        
        const awsLink = await this.convertPixabayLinkToAWS(this.imagePieceContent);
        return awsLink;       
      } else {        
        return this.imagePieceContent;
      }
    },
    async processImages() {    
      const newImageLink = await this.getImageLink();     
      const newImagePiece = {...this.imagePiece, content: newImageLink};   
      const newThemeImagePiece = this.themeImagePiece ? {...this.themeImagePiece, content: newImageLink} : null;   
        return [...this.socialPieces].map(piece => piece.id === this.imagePiece.id ? newImagePiece : piece.id === this.themeImagePiece?.id ? newThemeImagePiece : piece ); 
    },   
    async onSave() {    
      this.saving = true;         
      try {
        if(this.advancedMode) {
          if(!this.savedAsAdvanced) {
            await enterAdvancedMode(this.campaign.id, this.step.id, encodeURIComponent(this.linkPieceContent));          
          }        
        } else {
          this.clearLinkPiece();
          if(this.savedAsAdvanced) {
            await leaveAdvancedMode(this.campaign.id, this.step.id);
          }
        }
        let newPieces = this.imagePieceContent ? await this.processImages() : this.socialPieces; 
        if(this.advancedMode && !this.savedAsAdvanced) {
          newPieces = [...this.socialPieces].filter(piece => piece.name !== SocialPostPieceNames.Link);
        }        
        const data = {
          pieces: newPieces,
          id: this.step.id,
          status: this.enabled,
        };  
        await this.updateStep(data);        
        this.clearUndoRedo();
        this.$emit('step-completed');
        this.$emit('close');
      } catch(err) {
        $.notify('Something went wrong! Saving Failed!', 'error');
      } 
      this.saving = false;
    },
    getSocialProfile: async function (val) {
      try {
        let response = await getSocialNetworkProfile(val);
        if (response.error) {
          return $.notify(response.error || 'Something went wrong', 'warning');
        }
        this.profile = response;
      } catch (error) {
        $.notify(error.response?.data?.data?.error || 'Something went wrong', 'warning');
      }
    },
    async addTemporaryLinkPiece() { 
      if(!this.linkPiece) {
        this.socialPieces.push({
          content: '',
          name: SocialPostPieceNames.Link,
          id: generateUuid()
        });        
      } 
    }, 
    async getPieces() {
      const {data} = await getCampaignStepPieces(this.campaign.id, this.step.id);
      this.pieces = [...data];   
    }   
  },
  computed: { 
    savedAsAdvanced() {
      return this.linkPiece?.options?.advanced_mode;
    },
    classicLink() {
      return this.linkPieceContent?.includes('cloudfront') || this.linkPieceContent?.includes('financeinsights')
    },
    isPixabayImage() {
      return this.imagePieceContent.includes('pixabay.com');      
    },
    imagePiece() {
      return this.socialPieces.find(piece => piece.name === SocialPostPieceNames.ImagePreview);      
    },
    imagePieceContent() {
      return this.imagePiece?.content || '';
    },
    themeImagePiece() {          
      return this.socialPieces.find(piece => piece.name === SocialPostPieceNames.Image);      
    },  
    linkPiece() {
      return this.socialPieces.find(p => p.name === SocialPostPieceNames.Link);
    },
    linkPieceContent() {
      return this.linkPiece?.content;
    },
    socialNetwork() {
      return this.steps[this.activityType];
    },  
    menuItems() {
      return [
        {name: this.menuType.TextFields, icon: TextSvg},
        {name: this.menuType.Media, icon: ImagesSvg},        
        {name: this.menuType.AdvancedMode, icon: AdvancedSvg},
      ];
    },    
    iconClass() {
      const classes = {
        facebook: 'fab fa-facebook-f',
        linkedin: 'fab fa-linkedin-in',
        twitter: 'fab fa-twitter',
      };
      return classes[this.socialNetwork];
    },   
    disabledMenuItems() {
      const disabledItems = [];
      if (!this.imagePiece) {
        disabledItems.push(this.menuType.Media);
      }     
      return disabledItems;
    },
    advancedLink() {
      return `${this.advancedMode}|${this.linkPieceContent}`;
    },
  },
  watch: {
    pieces: {
      immediate: true,
      handler(val) {        
        this.socialPieces = [...val].filter(piece => !this.excludedFields.includes(piece.name));
      },
    },
    socialNetwork: {
      immediate: true,
      handler(val) {
        this.getSocialProfile(val);
      },
    },
    advancedLink: {
      immediate: true,
      handler(data) {
        const link = data.split('|')[1];        
        if (!this.advancedMode) {
          return;
        }
        if(!link) {
          this.linkDangerous = false
        }
        if (isValidUrl(link)) {          
          this.onCheckLink(link)
        }
      },
    },
  },
  async mounted() {   
    await this.getPieces();   
    this.$nextTick(() => {
      this.updateUndoRedo(this.socialPieces);      
      if(this.savedAsAdvanced) {
        this.onAdvanced();
      }
    });
  },
};
</script>

<style scoped lang="scss">
@import '../../../../../sass/web/_variables';

.social-step-editor {
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-template-columns: 1fr 279px 72px;
  min-height: 100vh;
  position: relative;
  @media (min-width: $breakpoint-xl) {
    grid-template-columns: 1fr 353px 72px;
    grid-template-rows: max-content 1fr;
  }
  &__header {
    grid-column: 1/4;
    align-items: center;
  }
  &__spinner {
    margin-top: 200px;
  }
  &__logo {
    font-size: 24px;
  }
  &__loader {   
    min-height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
